import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { getProviderData } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { getUserDevice } from "src/utils/common-util";
import { route } from "src/vars";

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 등록 완료 화면 (신용카드 등록, 변경)
 * /court/payment/result?bizType=payment&response=return&billId=6151&contractId=1030&pgcode=virtualaccount&partnerCode=&providerId=
 * 신용카드 등록 및 결제 결과를 표시하는 컴포넌트
 */
const RegisterCreditcardComplete = () => {
  const { t } = useTranslation(["contract", "common"]);

  // API 호출 훅
  const { executeAsync: findSellerData } = useApiOperation(getProviderData);

  // 네비게이션 및 라우팅 훅
  const navigate = usePartnerNavigate();
  const { contractId } = useParams();
  const location = useLocation();

  // 디바이스 정보
  const device = getUserDevice();

  // 판매자 정보 상태
  const [seller, setSeller] = useState<any>(null);

  // URL 쿼리 파라미터 파싱
  const query = useMemo(() => {
    return qs.parse(location.search, {
      allowDots: true,
      ignoreQueryPrefix: true,
    });
  }, [location.search]);

  // 신용카드 등록 완료 후 이동 경로
  const creditcardPath = `/court/mypage/contracts/${query.contractId}`;

  // 결제 완료 후 이동 경로
  const paymentPath = `/court/mypage/contracts/${query.contractId}/bills/${query.billOrder}`;
  const myMain = route.myContracts;

  // 결과 메시지 목록 정의
  const resultList = useMemo(() => {
    return [
      {
        response: "PAYMENT_SUCCESS",
        title: t("paymentResult.paymentSuccessTitle"),
        message: t("paymentResult.paymentSuccessMessage"),
        path: paymentPath,
        icon: "",
      },
      {
        response: "PAYMENT_FAILED",
        title: t("paymentResult.paymentFailedTitle"),
        message: t("paymentResult.paymentFailedMessage"),
        path: paymentPath,
        icon: "",
      },
      {
        response: "PAYMENT_CANCEL",
        title: t("paymentResult.paymentCancelTitle"),
        message: t("paymentResult.paymentCancelMessage"),
        path: paymentPath,
        icon: "",
      },
      {
        response: "return",
        title: query.errorCode ? t("paymentResult.registrationFailedTitle") : t("paymentResult.registrationCompletedTitle"),
        message: query.errorCode ? t("paymentResult.cardRegistrationFailedMessage") : t("paymentResult.cardRegistrationCompletedMessage"),
        path: creditcardPath,
        icon: "",
      },
      {
        response: "cancel",
        title: t("paymentResult.registrationCancelTitle"),
        message: t("paymentResult.cardRegistrationCancelMessage"),
        path: creditcardPath,
        icon: "",
      },
    ];
  }, [creditcardPath, paymentPath, query.errorCode, t]);

  // 판매자 정보 조회 함수
  const getSeller = useCallback(
    async (providerId: string) => {
      return await findSellerData({ providerId });
    },
    [findSellerData],
  );

  // 판매자 정보 설정 함수
  const fetchSeller = useCallback(
    async (providerId: string) => {
      const result: any = await getSeller(providerId);
      setSeller(result);
    },
    [getSeller],
  );

  // 판매자 정보 조회 효과
  useEffect(() => {
    if (query.providerId) {
      fetchSeller(String(query.providerId));
    }
  }, [fetchSeller, query]);

  // 판매자 연락처 정보
  const sellerInfo = useMemo(() => {
    if (seller && seller.data.data && seller.data.data.providerContactList) {
      const finded = seller.data.data.providerContactList.find((item: any) => item.assignedWorkType === "CS_PROVIDE");
      if (finded) {
        return finded.contact;
      }
    }
    return seller;
  }, [seller]);

  // 결과 항목 결정
  const resultItem: any = useMemo(() => {
    let item = resultList.find((item) => item.response === query.response);

    if (item && query.response === "return") {
      if (query.bizType === "payment") {
        if (query.pgcode === "creditcard") {
          item.title = query.errorCode ? t("paymentResult.paymentFailedTitle") : t("paymentResult.paymentSuccessTitle");
          item.message = query.errorCode ? String(query.payletterMessage) : t("paymentResult.paymentSuccessMessage");
          item.path = myMain.replace(":contractId", String(query.contractId));
        }
        if (query.pgcode === "virtualaccount") {
          item.title = query.errorCode ? t("paymentResult.virtualAccountCreationFailedTitle") : t("paymentResult.virtualAccountCreationTitle");
          item.path = myMain.replace(":contractId", String(query.contractId));
          item.message = query.errorCode ? String(query.payletterMessage) : t("paymentResult.virtualAccountCreationCompletedMessage");
        }
      }
      if (query.errorCode === "eCT800" && query.payletterMessage) {
        item.message = String(query.payletterMessage);
      }
    }

    if (item && query.response === "cancel") {
      if (query.bizType === "payment") {
        item.title = query.errorCode ? t("paymentResult.paymentCancelFailedTitle") : t("paymentResult.paymentRequestCancelTitle");
        item.message = query.errorCode ? String(query.errorCode) : t("paymentResult.paymentRequestCancelMessage");
        item.path = myMain.replace(":contractId", String(query.contractId));
      }
    }

    if (item && query.response === "PAYMENT_FAILED") {
      item.path = myMain.replace(":contractId", String(query.contractId));
      if (query.payletterMessage) {
        item.message = String(query.payletterMessage);
      } else {
        item.message = item.message.replace("#{contact}", sellerInfo);
      }
    }

    if (item && query.partnerCode !== "") {
      if (query.partnerCode && !item.path.includes("partner/")) {
        const newToArr = item.path.split("/");
        newToArr.splice(2, 0, `partner/${query.partnerCode}`);
        item.path = newToArr.join("/");
      }
    }
    return item;
  }, [resultList, query, myMain, sellerInfo, t]);

  // 마이페이지로 이동 함수
  const goToMyPage = () => {
    const path = resultItem?.path || "";
    navigate(path, { state: "mypage" });
  };

  return (
    <div>
      <form name="paymentCompleted">
        {/* 페이지 메타 태그 설정 */}
        <MetaTag title={getMypageTitle()} />
        {/* 결과 알림 섹션 */}
        <article className="announcement-page with-floating">
          <section>
            {/* 체크 아이콘 */}
            <div className="icon ic-check"></div>
            {/* 결과 메시지 */}
            <div className="message-wrap">
              <h2>{resultItem?.title}</h2>
              <p className="pre-formatted">{resultItem?.message}</p>
            </div>
            {/* 데스크톱 버전 확인 버튼 */}
            {device === "other" && (
              <div className="minmax500 px60 mt100">
                <button type="button" className="base-btn" onClick={goToMyPage}>
                  {t("common:confirm")}
                </button>
              </div>
            )}
          </section>
        </article>
        {/* 모바일 버전 확인 버튼 */}
        {device !== "other" && (
          <div className="base-floating-btn-wrap">
            <button type="button" className="base-btn" onClick={goToMyPage}>
              {t("common:confirm")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default RegisterCreditcardComplete;
